import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  DOCUMENT,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import {
  holdLayoutSettling,
  SuiOverlayOutletComponent,
  SuiSidebarOutletComponent,
  SuiSidebarService,
  SuiTheme,
  SuiThemeService,
  SuiToastService,
} from '@semeris/ui';
import { isEqual } from 'lodash-es';
import { combineLatest, fromEvent, Observable, of, Subscription } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { CommandPaletteOrchestratorService } from '../features/command-palette/services/command-palette-orchestrator.service';
import { NotesPanelComponent } from '../features/comment/containers/notes-panel/notes-panel.component';
import { ViewerCommand, XRayOptions } from '../features/document-viewer/models/document-viewer.models';
import { DocumentViewerService } from '../features/document-viewer/services/document-viewer.service';
import { HelpPanelComponent } from '../features/help/containers/help-panel/help-panel.component';
import { ActivityEvent } from '../features/home/models/activity-event.model';
import { AggregatedLoggerService } from '../features/logging/services/aggregated-logger.service';
import { NextUnavailableComponent } from '../features/shared/components/next-unavailable/next-unavailable.component';
import { WARNING_NOTIFICATION_CONFIGURATION } from '../features/shared/constants/notification-configurations';
import { PANEL_TRANSITION_MS } from '../features/shared/constants/panel-animation';
import { SemerisRoutes } from '../features/shared/constants/routes';
import { ClosableInputControllerDirective } from '../features/shared/directives/closable-input-controller.directive';
import { CopyToControllerDirective } from '../features/shared/directives/copy/copy-to-controller.directive';
import { Permission } from '../features/shared/models/api/permissions.enum';
import { ThemeMode } from '../features/shared/models/common/themes';
import { WebSocketEntities } from '../features/shared/models/web-socket/web-socket.models';
import { breadcrumbTitle } from '../features/shared/services/breadcrumb-title.strategy';
import { BreadcrumbService } from '../features/shared/services/breadcrumb.service';
import { DataService } from '../features/shared/services/data.service';
import { DeploymentSwitchService } from '../features/shared/services/deployment/deployment-switch.service';
import { PermissionService } from '../features/shared/services/permission.service';
import { UpdateService } from '../features/shared/services/update.service';
import { wsLink } from '../features/shared/utils/workspace-link';
import { AppSidebarComponent } from '../features/sidenav/components/app-sidebar/app-sidebar.component';
import { UnavailableComponent } from '../features/status-pages/unavailable.component';
import { SystemMessageOutletComponent } from '../features/system-message/components/system-message-outlet/system-message-outlet.component';
import { SystemMessageType } from '../features/system-message/models/system-message.model';
import { ToolbarComponent } from '../features/toolbar/components/toolbar/toolbar.component';
import { AppActions } from '../store/app/app.actions';
import { AppSelectors } from '../store/app/app.selectors';
import { DatapointLookupActions } from '../store/datapoint-lookup/datapoint-lookup.actions';
import { DealFilterActions } from '../store/deal-filter/deal-filter.actions';
import { DealFilterSelectors } from '../store/deal-filter/deal-filter.selectors';
import { DocumentViewerActions } from '../store/document-viewer/document-viewer.actions';
import { DocumentViewerSelectors } from '../store/document-viewer/document-viewer.selectors';
import { HomeActions } from '../store/home/home.actions';
import { LabelActions } from '../store/label/label.actions';
import { PanelActions } from '../store/panel/panel.actions';
import { PanelViewId } from '../store/panel/panel.state';
import { RootState } from '../store/root-state.model';
import { RootStoreActions } from '../store/root-store.actions';
import { TransactionActions } from '../store/transaction/transaction.actions';
import { UserProfileActions } from '../store/user-profile/user-profile.actions';
import { UserProfileSelectors } from '../store/user-profile/user-profile.selectors';

@Component({
  selector: 'semeris-root',
  templateUrl: './app.component.html',
  styleUrls: [],
  host: {
    class: 'flex min-h-screen',
    '(document:keydown)': 'onGlobalKeydown($event)',
  },
  imports: [
    ClosableInputControllerDirective,
    RouterOutlet,
    SuiSidebarOutletComponent,
    CopyToControllerDirective,
    ToolbarComponent,
    SystemMessageOutletComponent,
    NotesPanelComponent,
    HelpPanelComponent,
    AsyncPipe,
    SuiOverlayOutletComponent,
    NextUnavailableComponent,
    UnavailableComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly SystemMessageType = SystemMessageType;
  private readonly store = inject<Store<RootState>>(Store);
  private readonly logger = inject(AggregatedLoggerService);
  private readonly authService = inject(AuthService);
  private readonly dataService = inject(DataService);
  private readonly router = inject(Router);
  private readonly themeService = inject(SuiThemeService);
  private readonly metaService = inject(Meta);
  private readonly titleService = inject(Title);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly permissionService = inject(PermissionService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly route = inject(ActivatedRoute);
  private readonly documentViewerService = inject(DocumentViewerService);
  private readonly updateService = inject(UpdateService);
  public readonly sidebarService = inject(SuiSidebarService);
  private readonly cmdPaletteOrchestrator = inject(CommandPaletteOrchestratorService);
  private readonly toastService = inject(SuiToastService);
  private readonly deploymentSwitch = inject(DeploymentSwitchService);

  // The HOST, not the build variant — otherwise this branch would gate local development too.
  private readonly isNextHost = this.deploymentSwitch.isNextHost;

  private readonly isNextBlocked$ = this.permissionService.permissions$.pipe(map((permissions) => this.isNextBlockedFor(permissions)));

  /**
   * True when this host serves the `next` frontend and the user may not use it — the shell is
   * replaced by a notice. False while undetermined; the splash covers that window.
   */
  public readonly isNextBlocked = toSignal(this.isNextBlocked$, { initialValue: false });

  // A user with NO permissions is unverified or blocked, not denied — the shell routes those to
  // their own screens, so only deny someone who otherwise has access.
  private isNextBlockedFor(permissions: string[]): boolean {
    return this.isNextHost && permissions.length > 0 && !permissions.includes(Permission.READ_NEXT_UI);
  }

  public isAuthenticated$: Observable<boolean>;
  public isSmall$: Observable<boolean>;
  public isEmbedded$: Observable<boolean> = this.store.select(AppSelectors.isEmbedded);
  public appEnabled$: Observable<boolean> = this.store.select(AppSelectors.appEnabled);

  /** Where the user last headed, kept when a guard cancels that navigation and resets the address bar to `/`. */
  public readonly requestedUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
      map((event) => event.url),
    ),
    { initialValue: this.document.location.pathname + this.document.location.search },
  );

  private readonly profile = this.store.selectSignal(UserProfileSelectors.profile);
  private readonly profileError = this.store.selectSignal(UserProfileSelectors.error);

  /** The account failed to load: every guard and status page decides from it, so there is nothing to show. */
  public readonly isAccountUnavailable = computed(() => !!this.profileError() && !this.profile());

  /**
   * True when the active route is flagged `fullscreen` in its `Route.data` —
   * the shell then renders a bare `<router-outlet>` with no sidebar/toolbar
   * chrome (e.g. the first-time setup wizard).
   */
  public readonly isFullscreenRoute = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => {
        let route = this.route.snapshot;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return !!route.data?.fullscreen;
      }),
    ),
    { initialValue: false },
  );

  /** True while a route navigation is in flight — drives the top progress bar. */
  public readonly isNavigating = toSignal(
    this.router.events.pipe(
      filter(
        (e): e is NavigationStart | NavigationEnd | NavigationCancel | NavigationError =>
          e instanceof NavigationStart || e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError,
      ),
      map((e) => e instanceof NavigationStart),
    ),
    { initialValue: false },
  );

  private readonly themeColorEffect = effect(() => {
    const color = this.themeService.activeTheme() === SuiTheme.Dark ? '#000000' : '#008ad5';
    this.metaService.updateTag({ name: 'theme-color', content: color }, 'name="theme-color"');
  });

  private readonly documentTitleEffect = effect(() => this.titleService.setTitle(breadcrumbTitle(this.breadcrumbService.items())));

  @HostListener('window:beforeprint')
  public onBeforePrint() {
    this.store.dispatch(AppActions.SetPrintMode({ isPrintMode: true }));
    this.store.dispatch(PanelActions.SetCalculatedViewerState({ state: [PanelViewId.Summary] }));
  }

  @HostListener('window:afterprint')
  public onAfterPrint() {
    this.store.dispatch(AppActions.SetPrintMode({ isPrintMode: false }));
  }

  @HostListener('window:visibilitychange')
  public onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.initHeartbeat(0);
      this.updateService.checkForUpdate();
    } else {
      this.pauseHeartbeat();
    }
  }

  public onGlobalKeydown(event: KeyboardEvent): void {
    const mod = event.metaKey || event.ctrlKey;
    // Auto-repeat would flap a toggle shortcut for as long as the chord is held.
    if (!mod || event.repeat) {
      return;
    }

    // Chrome-less routes include the status pages, where the palette would be a way out.
    if (this.isFullscreenRoute()) {
      return;
    }

    // Cmd+K: Open command palette
    if (event.key === 'k') {
      event.preventDefault();
      this.cmdPaletteOrchestrator.open();
    }

    // Cmd+\: Toggle the sidebar
    if (event.key === '\\') {
      event.preventDefault();
      this.sidebarService.toggle();
    }
  }

  // `delay(0)` defers the `basis-*` flip a frame past mount; without it the transition never runs.
  public notesPanelOpen$ = this.store.select(AppSelectors.notesPanelOpen).pipe(delay(0));
  /** Drives the slot's width transition. */
  public notesPanelOpen = toSignal(this.notesPanelOpen$, { initialValue: false });
  /** DOM presence — lags `notesPanelOpen` on close so the panel can animate out before it unmounts. */
  public readonly notesPanelMounted = signal(false);

  public helpPanelOpen$ = this.store.select(AppSelectors.helpOpen).pipe(delay(0));
  public helpPanelOpen = toSignal(this.helpPanelOpen$, { initialValue: false });
  public readonly helpPanelMounted = signal(false);

  private heartBeatSubscription: Subscription;
  private readonly subscription$ = new Subscription();

  private xRayOptionsLookup: Record<XRayOptions, string> = {
    [XRayOptions.Entities]: 'entitiesEnabled',
    [XRayOptions.LegalLogic]: 'legalLogicEnabled',
    [XRayOptions.Reflow]: 'reflowEnabled',
  };

  constructor() {
    effect(() => {
      if (this.isAccountUnavailable()) {
        (window as any).removeSuiSplash?.();
      }
    });
    this.initSoftwareUpdater();
    this.trackPanelMount(this.notesPanelOpen, this.notesPanelMounted);
    this.trackPanelMount(this.helpPanelOpen, this.helpPanelMounted);
  }

  /**
   * Keeps a slide-in panel's DOM presence in step with its open state. Unmount must lag close, or
   * there's no element left to animate out.
   */
  private trackPanelMount(open: Signal<boolean>, mounted: WritableSignal<boolean>): void {
    let stateSeen = false;
    let unmountTimer: ReturnType<typeof setTimeout> | undefined;

    effect((onCleanup) => {
      const isOpen = open();

      // Also runs before the next toggle, so a reopen inside the window cancels the unmount.
      onCleanup(() => clearTimeout(unmountTimer));

      // First run is app start, not a toggle — holding the measurement pause there would
      // stall every overflow strip and scroll viewport for the first 300ms after boot.
      if (!stateSeen) {
        stateSeen = true;
        mounted.set(isOpen);
        return;
      }

      // The slot animates `flex-basis`, which resizes page content every frame.
      holdLayoutSettling(PANEL_TRANSITION_MS);

      if (isOpen) {
        mounted.set(true);
        return;
      }

      unmountTimer = setTimeout(() => mounted.set(false), PANEL_TRANSITION_MS);
    });
  }

  public readonly title = 'semeris';

  private initSoftwareUpdater() {
    this.updateService.init();
  }

  private checkBrowserSupport(): void {
    if (localStorage.getItem('semeris-browser-supported') === 'false') {
      this.toastService.warning(
        'Your browser may not be fully supported. For the best experience, use a recent version of Chrome, Edge, Firefox, or Safari.',
        { position: 'top-center', duration: 0 },
      );
    }
  }

  private trackSystemColorScheme(): void {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    this.store.dispatch(AppActions.SetSystemPrefersDark({ prefersDark: mq.matches }));
    const onChange = (e: MediaQueryListEvent): void => {
      this.store.dispatch(AppActions.SetSystemPrefersDark({ prefersDark: e.matches }));
    };
    mq.addEventListener('change', onChange);
    this.subscription$.add(() => mq.removeEventListener('change', onChange));
  }

  public ngOnInit(): void {
    console.timeEnd(`[AppComponent] ngOnInit`);
    this.sidebarService.configure(AppSidebarComponent, { mode: 'expanded' });
    this.sidebarService.enableResponsive();
    this.checkBrowserSupport();
    this.trackSystemColorScheme();
    const url = new URL(window.location.href);
    if (url.searchParams.has('iss') || url.searchParams.has('error_description')) {
      this.store.dispatch(AppActions.Logout());
    }

    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.App, id: 'webapp' }));
    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.Reset, id: 'webapp' }));
    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.AssetSetting }));
    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.Label }));

    this.subscription$.add(
      this.route.queryParamMap
        .pipe(
          map((p) => p.get('conversationId')),
          distinctUntilChanged(),
        )
        .subscribe((conversationId) => {
          if (conversationId) {
            this.store.dispatch(AppActions.ToggleNotesPanel({ open: true }));
          }
        }),
    );
    this.updateVh();

    this.subscription$.add(
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(250),
          tap(() => {
            this.updateVh();
          }),
        )
        .subscribe(),
    );

    this.subscription$.add(
      this.store
        .select(UserProfileSelectors.themePreference)
        .pipe(filter((mode): mode is ThemeMode => !!mode))
        .subscribe((mode) => {
          this.themeService.setMode(mode);
        }),
    );

    this.subscription$.add(
      this.store.select(AppSelectors.printMode).subscribe((printMode) => {
        this.themeService.setOverride(printMode ? SuiTheme.Light : null);
      }),
    );

    this.subscription$.add(
      this.documentViewerService.sentCommands$
        .pipe(filter((command) => command.commandType === ViewerCommand.SourceReference))
        .subscribe((command) => {
          this.store.dispatch(AppActions.AddActivity({ activity: ActivityEvent.SourceRefNavigate, meta: command }));
        }),
    );

    this.subscription$.add(
      this.store
        .select(DocumentViewerSelectors.xRayOptions)
        .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
        .subscribe((options?: XRayOptions[]) => {
          Object.keys(this.xRayOptionsLookup).forEach((option: XRayOptions) => {
            if (options?.includes(option)) {
              this.document.body.classList.add(this.xRayOptionsLookup[option]);
            } else {
              this.document.body.classList.remove(this.xRayOptionsLookup[option]);
            }
          });
        }),
    );

    this.isSmall$ = this.store.select(AppSelectors.isSmall);

    this.store.dispatch(AppActions.StartObservLayout());
    this.subscription$.add(
      combineLatest([
        this.router.events.pipe(
          filter((event) => event instanceof NavigationEnd),
          delay(0),
          tap((event: NavigationEnd) => {
            this.logger.navigation(event.url);
            this.store.dispatch(AppActions.SetUrl({ url: event.url }));
          }),
        ),
        this.permissionService.hasAnyPermission$.pipe(distinctUntilChanged()),
      ]).subscribe(([event, hasAnyPermission]) => {
        if (event.url === '/' || event.urlAfterRedirects === '/') {
          this.router.navigate(hasAnyPermission ? wsLink(SemerisRoutes.home.root) : wsLink(SemerisRoutes.welcome));
        }
      }),
    );

    this.initHeartbeat();

    window.addEventListener('offline', () => {
      this.store.dispatch(AppActions.EnterOfflineMode());
    });

    window.addEventListener('online', () => {
      this.store.dispatch(AppActions.RestoreFromOfflineMode());
    });

    this.isAuthenticated$ = this.authService.isAuthenticated$;

    this.authService.error$.pipe(filter((error) => !!error)).subscribe((error) => {
      console.error(error);
      // Auth0 exposes the stable error code on `error.error`; fall back to the human-readable message.
      const errorCode = (error as { error?: string }).error;
      const isLoginRequired = error.message === 'Login required';
      // A silent token refresh can't satisfy an interactive MFA challenge — recover by redirecting to login.
      const isMfaRequired = errorCode === 'mfa_required' || error.message === 'Multifactor authentication required';

      if (isLoginRequired || isMfaRequired) {
        this.authService.loginWithRedirect();
      }
    });

    // Permissions arrive with the profile, so a blocked user has already booted by this point.
    this.subscription$.add(
      this.isNextBlocked$
        .pipe(
          filter((isBlocked) => isBlocked),
          take(1),
        )
        .subscribe(() => {
          this.store.dispatch(HomeActions.StopRefreshToken());
          this.store.dispatch(TransactionActions.StopPollingTransactionChanges());
        }),
    );

    this.subscription$.add(
      this.authService.isAuthenticated$
        .pipe(
          distinctUntilChanged(),
          withLatestFrom(this.store.select(AppSelectors.isEmbedded)),
          tap(([isAuthenticated, isEmbedded]) => {
            if (isAuthenticated) {
              this.initSessionExpiredNotification();

              if (!isEmbedded) {
                this.store.dispatch(UserProfileActions.LoadProfile({ init: 'welcome' }));
                this.store.dispatch(AppActions.TriggerAppInit());
                this.store.dispatch(HomeActions.StartRefreshToken());
                this.store.dispatch(TransactionActions.StartPollingTransactionChanges());
                this.store.dispatch(DatapointLookupActions.LoadLookup());
              }
            } else {
              this.store.dispatch(HomeActions.StopRefreshToken());
              this.store.dispatch(TransactionActions.StopPollingTransactionChanges());
              this.store.dispatch(AppActions.SetAppEnabled({ appEnabled: true }));
            }
          }),
        )
        .subscribe(),
    );

    this.subscription$.add(
      combineLatest([this.store.select(AppSelectors.isEmbedded), this.authService.user$])
        .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
        .subscribe(([isEmbedded, userInfo]) => {
          if (isEmbedded && !!userInfo) {
            this.logger.setUser(userInfo.name, userInfo.email);
            this.store.dispatch(
              RootStoreActions.LoadFromLocalStorage({
                email: userInfo.email,
                displayName: userInfo.name,
              }),
            );
          }
        }),
    );

    this.subscription$.add(
      combineLatest([this.authService.user$, this.store.select(AppSelectors.initialized)])
        .pipe(
          distinctUntilChanged((a, b) => isEqual(a, b)),
          filter(([userInfo, initialized]) => !!userInfo && !!initialized),
        )
        .subscribe(([userInfo]) => {
          this.logger.setUser(userInfo.name, userInfo.email);
          this.store.dispatch(
            RootStoreActions.LoadFromLocalStorage({
              email: userInfo.email,
              displayName: userInfo.name,
            }),
          );
        }),
    );

    this.subscription$.add(
      this.store
        .select(AppSelectors.initialized)
        .pipe(
          distinctUntilChanged(),
          filter((initialized) => !!initialized),
          withLatestFrom(this.store.select(DealFilterSelectors.selectedFilter), this.store.select(DealFilterSelectors.list)),
        )
        .subscribe(([trigger, dealFilter, list]) => {
          this.store.dispatch(LabelActions.DownloadLabels());
          this.store.dispatch(DealFilterActions.UseDefaultFilter());
        }),
    );

    this.permissionService.permissions$
      .pipe(
        // A blocked user never reaches `initialized` — reveal the notice as soon as we know.
        switchMap((permissions) =>
          permissions.length > 0 && !this.isNextBlockedFor(permissions)
            ? this.store.select(AppSelectors.initialized).pipe(
                filter((initialized) => !!initialized),
                take(1),
              )
            : of(true),
        ),
        take(1),
      )
      .subscribe(() => {
        (window as any).removeSuiSplash?.();
      });

    this.permissionService.permissions$.pipe(distinctUntilChanged((a, b) => isEqual(a, b))).subscribe((permissions) => {
      this.store.dispatch(
        DocumentViewerActions.SetCommentsEnabled({
          enabled: permissions.includes(Permission.WRITE_COMMENT),
        }),
      );
    });
  }

  private initSessionExpiredNotification() {
    this.subscription$.add(
      combineLatest([this.authService.isLoading$, this.authService.isAuthenticated$]).subscribe(([isLoading, isAuthenticated]) => {
        if (!isAuthenticated && !window.location.href.includes(SemerisRoutes.auth.logout) && !isLoading) {
          this.store.dispatch(
            AppActions.PushNotification({
              message: `Your session has expired.`,
              action: `Login again`,
              closable: false,
              config: { ...WARNING_NOTIFICATION_CONFIGURATION, duration: 0 },
              id: 'reload_application',
            }),
          );
        }
      }),
    );
  }

  private initHeartbeat(dueTime?: number) {
    if (this.heartBeatSubscription) {
      return;
    }

    this.heartBeatSubscription = this.dataService
      .checkHeartbeat(dueTime)
      .pipe(
        withLatestFrom(this.store.select(AppSelectors.isOffline)),
        tap(([beat, isOffline]) => {
          if (beat && isOffline) {
            this.store.dispatch(AppActions.RestoreFromOfflineMode());
          }
        }),
      )
      .subscribe();
  }

  private pauseHeartbeat() {
    this.heartBeatSubscription?.unsubscribe();
    this.heartBeatSubscription = undefined;
  }

  private updateVh() {
    const vh = window.innerHeight * 0.01;

    this.document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  ngOnDestroy(): void {
    this.store.dispatch(AppActions.StopObservLayout());
    this.subscription$.unsubscribe();
  }
}
